export type CurrencyCode = 'INR' | 'USD' | 'EUR' | 'GBP' | 'AUD' | 'CAD' | 'SGD' | 'AED' | 'JPY' | 'CHF';

export const CURRENCY_OPTIONS: CurrencyCode[] = [
  'INR', 'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'SGD', 'AED', 'JPY', 'CHF',
];

const CURRENCY_SYMBOL_MAP: Record<CurrencyCode, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
  SGD: 'S$',
  AED: 'د.إ',
  JPY: '¥',
  CHF: 'CHF',
};

const CURRENCY_LOCALE_MAP: Record<CurrencyCode, string> = {
  INR: 'en-IN',
  USD: 'en-US',
  EUR: 'de-DE',
  GBP: 'en-GB',
  AUD: 'en-AU',
  CAD: 'en-CA',
  SGD: 'en-SG',
  AED: 'ar-AE',
  JPY: 'ja-JP',
  CHF: 'de-CH',
};

const CURRENCY_LABEL_MAP: Record<CurrencyCode, string> = {
  INR: 'Indian Rupee (₹)',
  USD: 'US Dollar ($)',
  EUR: 'Euro (€)',
  GBP: 'British Pound (£)',
  AUD: 'Australian Dollar (A$)',
  CAD: 'Canadian Dollar (C$)',
  SGD: 'Singapore Dollar (S$)',
  AED: 'UAE Dirham (د.إ)',
  JPY: 'Japanese Yen (¥)',
  CHF: 'Swiss Franc (CHF)',
};

export function isCurrencyCode(value: unknown): value is CurrencyCode {
  return typeof value === 'string' && CURRENCY_OPTIONS.includes(value as CurrencyCode);
}

export function currencySymbol(currency: CurrencyCode): string {
  return CURRENCY_SYMBOL_MAP[currency];
}

export function currencyLocale(currency: CurrencyCode): string {
  return CURRENCY_LOCALE_MAP[currency];
}

export function currencyLabel(currency: CurrencyCode): string {
  return CURRENCY_LABEL_MAP[currency];
}

export function formatCurrencyAmount(
  value: number,
  currency: CurrencyCode,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(currencyLocale(currency), {
    style: 'currency',
    currency,
    maximumFractionDigits: currency === 'JPY' ? 0 : currency === 'INR' ? 0 : 2,
    ...options,
  }).format(value);
}

export function formatCurrencyCodeAmount(value: number, currency: CurrencyCode): string {
  const formattedValue = new Intl.NumberFormat(currencyLocale(currency), {
    maximumFractionDigits: currency === 'JPY' ? 0 : 2,
  }).format(value);

  return `${currency} ${formattedValue}`;
}

/** PDF-safe currency symbols (ASCII only, for jsPDF Helvetica which can't render ₹, د.إ etc.) */
const PDF_SAFE_SYMBOL_MAP: Record<CurrencyCode, string> = {
  INR: 'Rs.',
  USD: '$',
  EUR: 'EUR',
  GBP: 'GBP',
  AUD: 'A$',
  CAD: 'C$',
  SGD: 'S$',
  AED: 'AED',
  JPY: 'JPY',
  CHF: 'CHF',
};

/** Format currency for jsPDF output — uses ASCII-safe symbols to avoid rendering issues */
export function formatCurrencyPdfSafe(value: number, currency: CurrencyCode): string {
  const formattedValue = new Intl.NumberFormat(currencyLocale(currency), {
    maximumFractionDigits: currency === 'JPY' ? 0 : 2,
  }).format(value);

  return `${PDF_SAFE_SYMBOL_MAP[currency]}${formattedValue}`;
}
